import { useTranslation } from 'react-i18next';

import { useMemo, useState } from 'react';

import { ColorSwatch, Combobox, Group, InputBase, useCombobox } from '@optiro/ui';

import { useCompanyAccountCategories } from '@/hooks/useCompanyAccountCategories/useCompanyAccountCategories';
import { CompanyAccountCategory } from '@/types/types';

const CLEAR_OPTION_VALUE = '$clear';

export interface SelectTradeProps {
  value: CompanyAccountCategory['id'] | null;
  onChange: (value: CompanyAccountCategory['id'] | null) => void;
  clearable?: boolean;
  ariaLabel?: string;
}

export const SelectTrade = ({ value, onChange, clearable, ariaLabel }: SelectTradeProps) => {
  const { t } = useTranslation('task');
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
    },
  });
  const { data: trades = [] } = useCompanyAccountCategories();
  const [search, setSearch] = useState('');

  const selected = useMemo(
    () => trades.find((trade) => String(trade.id) === String(value)) ?? null,
    [trades, value]
  );

  const trimmedQuery = search.trim();

  const filtered = useMemo(() => {
    const q = trimmedQuery.toLowerCase();
    if (!q) return trades;
    return trades.filter((trade) => trade.name.toLowerCase().includes(q));
  }, [trades, trimmedQuery]);

  const handleSelect = (submittedValue: string) => {
    if (submittedValue === CLEAR_OPTION_VALUE) {
      onChange(null);
    } else {
      const picked = trades.find((trade) => String(trade.id) === submittedValue);
      if (picked) onChange(picked.id);
    }
    combobox.closeDropdown();
  };

  return (
    <Combobox store={combobox} onOptionSubmit={handleSelect}>
      <Combobox.Target>
        <InputBase
          component="input"
          type="text"
          role="textbox"
          aria-label={ariaLabel}
          value={combobox.dropdownOpened ? search : (selected?.name ?? '')}
          placeholder={t('trade.placeholder')}
          leftSection={
            selected && !combobox.dropdownOpened ? (
              <ColorSwatch color={selected.color} size={12} />
            ) : undefined
          }
          rightSection={<Combobox.Chevron />}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          onChange={(event) => {
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
            setSearch(event.currentTarget.value);
          }}
        />
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options>
          {clearable && !trimmedQuery && (
            <Combobox.Option value={CLEAR_OPTION_VALUE} c="dimmed">
              {t('trade.none')}
            </Combobox.Option>
          )}
          {filtered.map((trade) => (
            <Combobox.Option value={String(trade.id)} key={trade.id}>
              <Group gap="xs" wrap="nowrap">
                <ColorSwatch color={trade.color} size={12} />
                {trade.name}
              </Group>
            </Combobox.Option>
          ))}
          {filtered.length === 0 && <Combobox.Empty>{t('trade.empty')}</Combobox.Empty>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default SelectTrade;
